import { useMemo, useState, useCallback, useEffect, useRef, ReactNode, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/layouts/Header";
import { Footer } from "@/layouts/Footer";
import { SEOHead } from "@/features/shared/SEOHead";
import {
  automotiveContentMap,
  AutomotiveVariantKey,
  AutomotivePageContent,
  AUTOMOTIVE_BOOKING_URL
} from "@/data/automotive";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { ArrowRight, Check, Sparkles, Star } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

// Critical above-the-fold components - load immediately
import { AutomotiveHero } from "@/components/ui/automotive-hero";
import { useRevealObserver } from "@/hooks/useRevealObserver";
import { AutomotiveIndustriesSection } from "@/components/ui/automotive-industries-section";
import { AutomotiveCredibilitySection } from "@/components/ui/automotive-credibility-section";
import { CapabilitiesSection } from "@/components/Homepage/CapabilitiesSection";
import { AutomotiveProblemsSection } from "@/components/ui/automotive-problems-section";

// Below-the-fold components - lazy load
const AutomotiveSystemsSection = lazy(() => import("@/components/ui/automotive-systems-section").then(m => ({ default: m.AutomotiveSystemsSection })));
const AutomotiveFeaturesSection = lazy(() => import("@/components/ui/automotive-features-section").then(m => ({ default: m.AutomotiveFeaturesSection })));
const AutomotiveDeepProblemsSection = lazy(() => import("@/components/ui/automotive-deep-problems-section").then(m => ({ default: m.AutomotiveDeepProblemsSection })));
const AutomotiveScaleProofSection = lazy(() => import("@/components/ui/automotive-scale-proof-section").then(m => ({ default: m.AutomotiveScaleProofSection })));
const AutomotiveRolloutSection = lazy(() => import("@/components/ui/automotive-rollout-section").then(m => ({ default: m.AutomotiveRolloutSection })));
const AutomotiveTiersSection = lazy(() => import("@/components/ui/automotive-tiers-section").then(m => ({ default: m.AutomotiveTiersSection })));
const NeedACarHighlight = lazy(() => import("@/components/case-studies/NeedACarHighlight").then(m => ({ default: m.NeedACarHighlight })));
const AutomotiveStackSection = lazy(() => import("@/components/ui/automotive-stack-section").then(m => ({ default: m.AutomotiveStackSection })));
const AutomotiveResourcesSection = lazy(() => import("@/components/ui/automotive-resources-section").then(m => ({ default: m.AutomotiveResourcesSection })));
const AutomotiveAISupportSection = lazy(() => import("@/components/ui/automotive-ai-support-section").then(m => ({ default: m.AutomotiveAISupportSection })));
const AutomotiveWhyChooseSection = lazy(() => import("@/components/ui/automotive-why-choose-section").then(m => ({ default: m.AutomotiveWhyChooseSection })));
const AutomotiveProofSection = lazy(() => import("@/components/ui/automotive-proof-section").then(m => ({ default: m.AutomotiveProofSection })));
const AutomotiveFAQSection = lazy(() => import("@/components/ui/automotive-faq-section").then(m => ({ default: m.AutomotiveFAQSection })));
const AutomotiveAddonsSection = lazy(() => import("@/components/ui/automotive-addons-section").then(m => ({ default: m.AutomotiveAddonsSection })));
const AutomotiveStarterOfferSection = lazy(() => import("@/components/ui/automotive-starter-offer-section").then(m => ({ default: m.AutomotiveStarterOfferSection })));
const AutomotiveClosingNoteSection = lazy(() => import("@/components/ui/automotive-closing-note-section").then(m => ({ default: m.AutomotiveClosingNoteSection })));
const DealershipProfitCalculator = lazy(() => import("@/components/tools/DealershipProfitCalculator").then(m => ({ default: m.DealershipProfitCalculator })));
const AutomotiveComparisonSection = lazy(() => import("@/components/ui/automotive-comparison-section").then(m => ({ default: m.AutomotiveComparisonSection })));
const AutomotivePillarsSection = lazy(() => import("@/components/ui/automotive-pillars-section").then(m => ({ default: m.AutomotivePillarsSection })));
const AutomotiveAIShowcaseSection = lazy(() => import("@/components/ui/automotive-ai-showcase-section").then(m => ({ default: m.AutomotiveAIShowcaseSection })));
const AutomotiveInventoryEngineSection = lazy(() => import("@/components/ui/automotive-inventory-engine-section").then(m => ({ default: m.AutomotiveInventoryEngineSection })));
const AutomotiveWebsiteSeoSection = lazy(() => import("@/components/ui/automotive-website-seo-section").then(m => ({ default: m.AutomotiveWebsiteSeoSection })));

// The calculator chunk carries recharts (~376KB). Don't fetch it until the section
// approaches the viewport. navigator.webdriver = the prerenderer: render immediately so
// the static HTML keeps the section (prerender-safe-motion guardrail).
function DeferredCalculator() {
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(() => typeof navigator !== "undefined" && navigator.webdriver === true);
  useEffect(() => {
    if (show) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setShow(true);
      return;
    }
    // Arm the observer only on first interaction: at first paint the lazy sections
    // above haven't rendered, so the page is short and this placeholder sits inside
    // the observer margin; observing immediately fetches the chunk on load anyway.
    let io: IntersectionObserver | null = null;
    const events: (keyof WindowEventMap)[] = ["scroll", "pointerdown", "touchstart", "keydown"];
    const disarm = () => events.forEach((ev) => window.removeEventListener(ev, arm));
    const arm = () => {
      disarm();
      io = new IntersectionObserver(
        (entries) => entries.some((e) => e.isIntersecting) && setShow(true),
        { rootMargin: "600px 0px" }
      );
      io.observe(el);
    };
    events.forEach((ev) => window.addEventListener(ev, arm, { passive: true }));
    return () => {
      disarm();
      io?.disconnect();
    };
  }, [show]);
  return (
    <div ref={ref} className={show ? undefined : "min-h-[480px]"}>
      {show && (
        <Suspense fallback={null}>
          <DealershipProfitCalculator />
        </Suspense>
      )}
    </div>
  );
}

const ORGANIZATION_SCHEMA = {
  "@type": "Organization",
  "@id": "https://frayze.ca/#organization",
  "name": "Frayze Technologies Inc.",
  "url": "https://frayze.ca/",
  "logo": "https://frayze.ca/Frayze%20Logo%20New%20light%20-%20landscape%20.png",
  "description": "Canada's leading digital agency specializing in CRM systems, lead generation, web development & custom AI solutions.",
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+1-807-700-0079",
    "contactType": "customer service",
    "areaServed": "CA",
    "availableLanguage": "English",
    "email": "hello@frayze.ca"
  },
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "202 Franklin St. North",
    "addressLocality": "Thunder Bay",
    "addressRegion": "ON",
    "postalCode": "P7C 4H9",
    "addressCountry": "CA"
  },
  "sameAs": [
    "https://www.linkedin.com/company/frayze/",
    "https://facebook.com/frayze.ca",
    "https://instagram.com/frayze.ca",
    "https://x.com/FrayzeIncCanada",
    "https://youtube.com/@frayzetech"
  ]
};

const LOCAL_BUSINESS_SCHEMA = {
  "@type": "LocalBusiness",
  "@id": "https://frayze.ca/#localbusiness",
  "name": "Frayze Technologies Inc.",
  "image": "https://frayze.ca/Frayze%20Logo%20New%20light%20-%20landscape%20.png",
  "telephone": "+1-807-700-0079",
  "email": "hello@frayze.ca",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "202 Franklin St. North",
    "addressLocality": "Thunder Bay",
    "addressRegion": "ON",
    "postalCode": "P7C 4H9",
    "addressCountry": "CA"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 48.3809,
    "longitude": -89.2477
  },
  "url": "https://frayze.ca/",
  "openingHours": "Mo-Fr 09:00-18:00",
  "priceRange": "$500-$2500",
  "description": "Canada's leading digital agency specializing in CRM systems, lead generation, web development & custom AI solutions.",
  "serviceArea": {
    "@type": "Country",
    "name": "Canada"
  }
};

interface AutomotivePageTemplateProps {
  variant: AutomotiveVariantKey;
}

const buildStructuredData = (content: AutomotivePageContent) => {
  const offerList = content.offers.map((offer) => {
    const numeric = offer.price.replace(/[^0-9.]/g, "");
    const priceSpecification = numeric
      ? {
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: Number(numeric),
          priceCurrency: "CAD",
          unitText: "MON",
          referenceQuantity: {
            "@type": "QuantitativeValue",
            unitCode: "MON",
            value: 1
          }
        }
      }
      : {};

    return {
      "@type": "Offer",
      name: offer.name,
      description: offer.description,
      url: `https://frayze.ca${content.slug}?package=${offer.id}`,
      ...priceSpecification
    };
  });



  const breadcrumbSchema = {
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://frayze.ca/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Automotive",
        "item": "https://frayze.ca/automotive"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": content.serviceSchemaName,
        "item": content.meta.canonical
      }
    ]
  };

  return {
    "@context": "https://schema.org",
    "@graph": [
      ORGANIZATION_SCHEMA,
      LOCAL_BUSINESS_SCHEMA,
      breadcrumbSchema,
      {
        "@type": "Service",
        "@id": `${content.meta.canonical}#service`,
        name: content.serviceSchemaName,
        url: content.meta.canonical,
        serviceType: content.serviceSchemaName,
        areaServed: [
          {
            "@type": "Country",
            name: "Canada"
          },
          {
            "@type": "Country",
            name: "United States"
          }
        ],
        provider: {
          "@type": "Organization",
          "@id": "https://frayze.ca/#organization"
        },
        description: content.meta.description,
        category: "Automotive",
        audience: {
          "@type": "Audience",
          audienceType: content.hero.audienceBullets ?? ["Automotive businesses"]
        },
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: `${content.meta.title} Packages`,
          itemListElement: offerList
        }
      }
    ]
  };
};

export function AutomotivePageTemplate({ variant }: AutomotivePageTemplateProps) {
  const content = automotiveContentMap[variant];
  const [isDemoOpen, setIsDemoOpen] = useState(false);
  useRevealObserver();
  const createCTAElement = useCallback(
    (href: string, children: ReactNode): ReactNode => {
      if (/^https?:\/\//.test(href)) {
        return (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        );
      }
      return <Link to={href}>{children}</Link>;
    },
    []
  );

  const goToBooking = useCallback(() => {
    if (typeof window !== "undefined") {
      window.open(AUTOMOTIVE_BOOKING_URL, "_blank", "noopener,noreferrer");
    }
  }, []);

  const structuredData = useMemo(() => buildStructuredData(content), [content]);

  const noindex = useMemo(() => {
    return ["marine", "powerSports"].includes(variant);
  }, [variant]);

  return (
    <div className="min-h-screen bg-[#FBFAF7] text-[#101319] font-sans antialiased overflow-x-hidden">
      <SEOHead
        title={content.meta.title}
        description={content.meta.description}
        keywords={content.meta.keywords}
        ogTitle={content.meta.ogTitle}
        ogDescription={content.meta.ogDescription}
        canonical={content.meta.canonical}
        structuredData={structuredData}
        noindex={noindex}
      />

      <Header />

      <main className="pb-32">
        <div className="max-w-[1312px] mx-auto px-6 py-4 relative z-20 pt-32">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/" className="text-[#5A6270] hover:text-[#101319]">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-[#C9C4B8]" />
              <BreadcrumbItem>
                <BreadcrumbLink href="/automotive" className="text-[#5A6270] hover:text-[#101319]">Automotive</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-[#C9C4B8]" />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-[#101319] font-bold">{content.serviceSchemaName}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <AutomotiveHero
          onDemoClick={goToBooking}
          bookingUrl={AUTOMOTIVE_BOOKING_URL}
          eyebrow={content.hero.eyebrow}
          title={content.hero.title}
          description={content.hero.description}
          supporting={content.hero.supporting}
          primaryCta={content.hero.primaryCta}
          secondaryCta={content.hero.secondaryCta}
        />

        <AutomotiveIndustriesSection />

        <AutomotiveCredibilitySection />

        <AutomotiveProblemsSection />

        <Suspense fallback={null}>
          {content.comparison && (
            <AutomotiveComparisonSection
              eyebrow={content.comparison.eyebrow}
              heading={content.comparison.heading}
              intro={content.comparison.intro}
              toolsRunLine={content.comparison.toolsRunLine}
              columns={content.comparison.columns}
              rows={content.comparison.rows}
              wedge={content.comparison.wedge}
            />
          )}
        </Suspense>

        <section className="border-y border-[#E9E6DE] bg-[#F4F2EC] hidden">
          <div className="max-w-[1312px] mx-auto px-6 py-10">
            <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
              <div className="space-y-6 text-center lg:text-left">
                <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-[#5A6270] lg:justify-start">
                  <Sparkles className="h-4 w-4 text-[#2F5FE8]" />
                  <span>{content.credibility}</span>
                </div>
                {content.credibilityNotes && content.credibilityNotes.length > 0 && (
                  <div className="mx-auto flex max-w-2xl flex-col gap-2 text-xs text-[#5A6270] md:text-sm lg:mx-0">
                    {content.credibilityNotes.map((note) => (
                      <span key={note}>{note}</span>
                    ))}
                  </div>
                )}
                {content.credibilityCard && (
                  <div className="mx-auto max-w-xl rounded-2xl border border-[#E9E6DE] bg-white p-6 shadow-sm lg:mx-0">
                    <div className="text-xs font-semibold uppercase tracking-[0.16em] font-mono-ibm text-[#2F5FE8]">{content.credibilityCard.title}</div>
                    <p className="mt-3 text-lg font-semibold text-[#101319]">{content.credibilityCard.stats}</p>
                    {content.credibilityCard.description && (
                      <p className="mt-3 text-sm text-[#5A6270]">{content.credibilityCard.description}</p>
                    )}
                  </div>
                )}
              </div>
              {content.reviewWidget && (
                <div className="mx-auto w-full max-w-lg rounded-2xl border border-[#E9E6DE] bg-white p-6 shadow-sm lg:p-8">
                  <div className="text-center">
                    <div className="text-xs font-semibold uppercase tracking-[0.16em] font-mono-ibm text-[#2F5FE8]">{content.reviewWidget.heading}</div>
                    <p className="mt-2 text-sm text-[#5A6270]">{content.reviewWidget.intro}</p>
                    <div className="mt-6 flex flex-col items-center gap-2">
                      <div className="text-5xl font-bold text-[#101319]">{content.reviewWidget.rating}</div>
                      <div className="flex items-center gap-2 text-sm text-[#5A6270]">
                        <span className="flex items-center gap-1 text-[#2F5FE8]">
                          <Star className="h-4 w-4 fill-[#2F5FE8] text-[#2F5FE8]" />
                          <Star className="h-4 w-4 fill-[#2F5FE8] text-[#2F5FE8]" />
                          <Star className="h-4 w-4 fill-[#2F5FE8] text-[#2F5FE8]" />
                          <Star className="h-4 w-4 fill-[#2F5FE8] text-[#2F5FE8]" />
                          <Star className="h-4 w-4 fill-[#2F5FE8] text-[#2F5FE8]" />
                        </span>
                        <span>{content.reviewWidget.reviewCount}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 space-y-3 text-left">
                    {content.reviewWidget.highlights.map((line) => (
                      <div key={line} className="flex items-start gap-2 rounded-xl bg-[#F4F2EC] p-3 text-sm text-[#5A6270]">
                        <Check className="mt-0.5 h-4 w-4 text-[#1B9E6B]" />
                        <span>{line}</span>
                      </div>
                    ))}
                  </div>

                  {content.reviewWidget.testimonials && content.reviewWidget.testimonials.length > 0 && (
                    <div className="mt-6 space-y-4">
                      {content.reviewWidget.testimonials.map((item) => (
                        <div key={item.quote} className="rounded-xl border border-[#E9E6DE] bg-white p-4 text-left shadow-sm">
                          <p className="text-sm text-[#5A6270] leading-relaxed">"{item.quote}"</p>
                          <div className="mt-3 text-xs font-semibold uppercase tracking-[0.16em] font-mono-ibm text-[#5A6270]">
                            {item.author}
                            {item.role && <span className="ml-2 text-[#5A6270]">{item.role}</span>}
                            {item.date && <span className="ml-2 text-[#8A8FA0]">{item.date}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {content.reviewWidget.ctaHref && content.reviewWidget.ctaLabel && (
                    <Button className="mt-6 w-full border border-[#E9E6DE] bg-white text-[#101319] hover:bg-[#F4F2EC]" asChild>
                      <Link to={content.reviewWidget.ctaHref}>{content.reviewWidget.ctaLabel}</Link>
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        <Suspense fallback={null}>
          {content.scaleProof && (
            <AutomotiveScaleProofSection
              heading={content.scaleProof.heading}
              description={content.scaleProof.description}
              bullets={content.scaleProof.bullets || []}
            />
          )}
        </Suspense>

        <Suspense fallback={null}>
          {content.deepProblems && (
            <AutomotiveDeepProblemsSection
              heading={content.deepProblems.heading}
              items={content.deepProblems.items}
            />
          )}
        </Suspense>

        <Suspense fallback={null}>
          {content.systems && content.systems.length > 0 ? (
            <AutomotiveSystemsSection
              systems={content.systems}
              eyebrow={content.systemsIntro?.eyebrow}
              title={content.systemsIntro?.title}
              description={content.systemsIntro?.description}
            />
          ) : content.features && content.features.length > 0 ? (
            <AutomotiveFeaturesSection features={content.features} />
          ) : null}
        </Suspense>

        <Suspense fallback={null}>
          {content.sixPillars && (
            <AutomotivePillarsSection
              eyebrow={content.sixPillars.eyebrow}
              heading={content.sixPillars.heading}
              intro={content.sixPillars.intro}
              pillars={content.sixPillars.pillars}
            />
          )}
        </Suspense>

        <Suspense fallback={null}>
          {content.aiShowcase && (
            <AutomotiveAIShowcaseSection
              eyebrow={content.aiShowcase.eyebrow}
              heading={content.aiShowcase.heading}
              intro={content.aiShowcase.intro}
              capabilities={content.aiShowcase.capabilities}
              proof={content.aiShowcase.proof}
            />
          )}
        </Suspense>

        <Suspense fallback={null}>
          {content.inventoryEngine && (
            <AutomotiveInventoryEngineSection
              eyebrow={content.inventoryEngine.eyebrow}
              heading={content.inventoryEngine.heading}
              intro={content.inventoryEngine.intro}
              features={content.inventoryEngine.features}
              proof={content.inventoryEngine.proof}
              ctaLabel={content.inventoryEngine.ctaLabel}
              ctaHref={content.inventoryEngine.ctaHref}
            />
          )}
        </Suspense>

        <Suspense fallback={null}>
          {content.websiteSeo && (
            <AutomotiveWebsiteSeoSection
              eyebrow={content.websiteSeo.eyebrow}
              heading={content.websiteSeo.heading}
              intro={content.websiteSeo.intro}
              tracks={content.websiteSeo.tracks}
              proof={content.websiteSeo.proof}
            />
          )}
        </Suspense>

        <Suspense fallback={null}>
          {content.recommendedRollout && (
            <AutomotiveRolloutSection
              heading={content.recommendedRollout.heading}
              intro={content.recommendedRollout.intro}
              note={content.recommendedRollout.note}
              steps={content.recommendedRollout.steps}
            />
          )}
        </Suspense>

        <Suspense fallback={null}>
          {content.stack && (
            <AutomotiveStackSection
              heading={content.stack.heading}
              subheading={content.stack.subheading}
              rows={content.stack.rows}
            />
          )}
        </Suspense>

        <CapabilitiesSection className="py-16 md:py-28 bg-[#F4F2EC]" subtitle="One connected system that captures every lead, automates the busywork, and books more jobs, so you can focus on the work." />

        <Suspense fallback={null}>
          {content.resourcesLinks && content.resourcesLinks.length > 0 && (
            <AutomotiveResourcesSection resources={content.resourcesLinks} />
          )}
        </Suspense>


        <Suspense fallback={null}>
          <section className="py-16 md:py-28 bg-white">
            <div className="max-w-[1312px] mx-auto px-6">
              <div className="text-center mb-16">
                <span className="inline-flex items-center gap-2.5 font-mono-ibm text-xs tracking-[0.16em] uppercase mb-6 text-[#2F5FE8]">
                  <span className="w-6 h-[1px] bg-[#2F5FE8]"></span>
                  The proof
                </span>
                <h2 className="text-3xl sm:text-4xl md:text-[48px] leading-[1.04] tracking-[-0.035em] font-semibold text-[#101319]">The system, proven across six rooftops.</h2>
                <p className="text-base md:text-lg text-[#5A6270] mt-6 max-w-2xl mx-auto">
                  Everything above isn't theory: it's what our founder built and ran for a multi-rooftop GTA dealer group before Frayze existed.
                </p>
              </div>
              <NeedACarHighlight variant="featured" focus="automotive" theme="blue" />
            </div>
          </section>
        </Suspense>

        <Suspense fallback={null}>
          {content.tiers && (
            <AutomotiveTiersSection
              eyebrow={content.tiers.eyebrow}
              heading={content.tiers.heading}
              intro={content.tiers.intro}
              tiers={content.tiers.tiers}
              note={content.tiers.note}
              ctaLabel={content.tiers.ctaLabel}
              ctaHref={content.tiers.ctaHref}
            />
          )}
        </Suspense>

        <Suspense fallback={null}>
          {content.starterOffer && (
            <AutomotiveStarterOfferSection offer={content.starterOffer} />
          )}
        </Suspense>

        <Suspense fallback={null}>
          {content.addons && (
            <AutomotiveAddonsSection
              heading={content.addons.heading}
              subheading={content.addons.subheading}
              items={content.addons.items}
              footer={content.addons.footer}
            />
          )}
        </Suspense>

        <Suspense fallback={null}>
          {content.aiSupport && (
            <AutomotiveAISupportSection
              heading={content.aiSupport.heading}
              description={content.aiSupport.description}
              bullets={content.aiSupport.bullets}
            />
          )}
        </Suspense>

        <Suspense fallback={null}>
          {content.whyChoose && (
            <AutomotiveWhyChooseSection
              heading={content.whyChoose.heading}
              bullets={content.whyChoose.bullets}
              quotes={content.whyChoose.quotes}
              footer={content.whyChoose.footer}
            />
          )}
        </Suspense>

        <Suspense fallback={null}>
          <AutomotiveProofSection
            eyebrow={content.proof.eyebrow}
            statement={content.proof.statement}
            supporting={content.proof.supporting}
            linkHref={content.proof.linkHref}
            linkLabel={content.proof.linkLabel}
          />
        </Suspense>

        <Suspense fallback={null}>
          <AutomotiveFAQSection faqs={content.faq} />
        </Suspense>

        {/* Explore Solutions: internal link bridge */}
        <section className="max-w-[1312px] mx-auto px-6 py-16 md:py-28">
          <h2 className="text-3xl sm:text-4xl md:text-[48px] leading-[1.04] tracking-[-0.035em] font-semibold text-center mb-6 text-[#101319]">Sell More Vehicles, Book More Service</h2>
          <p className="text-center text-[#5A6270] mb-16 max-w-2xl mx-auto text-base md:text-lg">
            Choose the system that solves your biggest bottleneck: slow lead response, an empty service bay, or stalled ad spend.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/solutions/crm-automation/" className="group p-6 border border-[#E9E6DE] rounded-2xl hover:border-[#2F5FE8]/50 hover:shadow-lg bg-white transition-all">
              <h3 className="font-semibold text-lg mb-2 text-[#101319]">CRM & Follow-Up System</h3>
              <p className="text-sm text-[#5A6270]">Respond to every lead in seconds and turn more enquiries into booked test drives.</p>
              <span className="text-sm font-medium text-[#2F5FE8] mt-3 inline-flex items-center gap-1">See how it works <ArrowRight className="w-3 h-3" /></span>
            </Link>
            <Link to="/solutions/lead-generation-system/" className="group p-6 border border-[#E9E6DE] rounded-2xl hover:border-[#2F5FE8]/50 hover:shadow-lg bg-white transition-all">
              <h3 className="font-semibold text-lg mb-2 text-[#101319]">Lead Generation Engine</h3>
              <p className="text-sm text-[#5A6270]">Keep your sales floor busy with a steady, predictable flow of qualified buyers.</p>
              <span className="text-sm font-medium text-[#2F5FE8] mt-3 inline-flex items-center gap-1">See how it works <ArrowRight className="w-3 h-3" /></span>
            </Link>
            <Link to="/solutions/ad-management-scale/" className="group p-6 border border-[#E9E6DE] rounded-2xl hover:border-[#2F5FE8]/50 hover:shadow-lg bg-white transition-all">
              <h3 className="font-semibold text-lg mb-2 text-[#101319]">Scale System (Ads)</h3>
              <p className="text-sm text-[#5A6270]">Google & Meta ad management tied to real revenue, so you know every dollar pays off.</p>
              <span className="text-sm font-medium text-[#2F5FE8] mt-3 inline-flex items-center gap-1">See how it works <ArrowRight className="w-3 h-3" /></span>
            </Link>
            <Link to="/solutions/websites/" className="group p-6 border border-[#E9E6DE] rounded-2xl hover:border-[#2F5FE8]/50 hover:shadow-lg bg-white transition-all">
              <h3 className="font-semibold text-lg mb-2 text-[#101319]">Web Design & Local SEO</h3>
              <p className="text-sm text-[#5A6270]">Get found by local buyers and turn website visits into leads for your dealership.</p>
              <span className="text-sm font-medium text-[#2F5FE8] mt-3 inline-flex items-center gap-1">See how it works <ArrowRight className="w-3 h-3" /></span>
            </Link>
            <Link to="/solutions/ai-strategy-rollout/" className="group p-6 border border-[#E9E6DE] rounded-2xl hover:border-[#2F5FE8]/50 hover:shadow-lg bg-white transition-all">
              <h3 className="font-semibold text-lg mb-2 text-[#101319]">Enterprise AI</h3>
              <p className="text-sm text-[#5A6270]">Let AI qualify, route, and answer every lead 24/7, so none slip through after hours.</p>
              <span className="text-sm font-medium text-[#2F5FE8] mt-3 inline-flex items-center gap-1">See how it works <ArrowRight className="w-3 h-3" /></span>
            </Link>
            <Link to="/case-studies/need-a-car-automotive-growth/" className="group p-6 border border-[#E9E6DE] rounded-2xl hover:border-[#2F5FE8]/50 hover:shadow-lg bg-white transition-all">
              <h3 className="font-semibold text-lg mb-2 text-[#101319]">Need A Car: 400% More Appointments</h3>
              <p className="text-sm text-[#5A6270]">See how AI follow-up automation turned more leads into booked appointments.</p>
              <span className="text-sm font-medium text-[#1B9E6B] mt-3 inline-flex items-center gap-1">Read case study <ArrowRight className="w-3 h-3" /></span>
            </Link>
          </div>
          <div className="mt-12 text-center">
            <p className="text-sm text-[#5A6270] mb-6">Or go straight to a specific capability:</p>
            <div className="flex flex-wrap justify-center gap-2.5">
              {[
                { to: "/platform/missed-call-text-back/", label: "Missed-Call Text-Back" },
                { to: "/platform/speed-to-lead/", label: "Speed-to-Lead" },
                { to: "/platform/ai-receptionist/", label: "AI Receptionist" },
                { to: "/platform/database-reactivation/", label: "Database Reactivation" },
                { to: "/platform/reputation-management/", label: "Reputation Management" },
                { to: "/platform/business-phone-system/", label: "Business Phone System" },
              ].map((c) => (
                <Link key={c.to} to={c.to} className="inline-flex items-center gap-1 rounded-full border border-[#E9E6DE] bg-white px-4 py-2 text-sm font-medium text-[#5A6270] hover:border-[#2F5FE8] hover:text-[#2F5FE8] transition-colors">
                  {c.label} <ArrowRight className="w-3 h-3" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Only show Calculator for General Automotive variants for now */}
        {content.key === 'general' && <DeferredCalculator />}
      </main>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[#E9E6DE] bg-white/95 backdrop-blur-sm">
        <div className="max-w-[1312px] mx-auto px-6 py-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="text-sm md:text-base font-medium text-[#101319]">
            {content.stickyCta.heading}
            <span className="ml-2 text-[#5A6270] hidden md:inline">
              {content.stickyCta.secondary}
            </span>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={goToBooking} className="px-8 py-3 rounded-xl bg-[#2F5FE8] hover:bg-[#2049BE] text-white font-bold text-sm transition-all">
              See it in action
            </button>
            {createCTAElement(AUTOMOTIVE_BOOKING_URL, <button className="px-8 py-3 rounded-xl border border-[#E9E6DE] text-[#101319] font-bold text-sm hover:border-[#2F5FE8] hover:text-[#2F5FE8] transition-colors">Book your free 20-min consult</button>)}
          </div>
        </div>
      </div>

      <div className="pb-24">
        <Footer />
      </div>

      <Dialog open={isDemoOpen} onOpenChange={setIsDemoOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{content.hero.primaryCta}</DialogTitle>
            <DialogDescription>{content.demo.heading}</DialogDescription>
          </DialogHeader>
          <AspectRatio ratio={16 / 9} className="rounded-xl overflow-hidden bg-black">
            <iframe
              src={content.demo.url}
              title="Frayze Automotive Demo"
              className="h-full w-full border-0"
              allow="autoplay; fullscreen"
              allowFullScreen
            />
          </AspectRatio>
          <p className="text-xs text-muted-foreground mt-4">
            {content.demo.description}
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
